import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import { GetMainSidebarInfo } from '../ChannelsSection/useChannels'

/**
 * GraphQL query to fetch all organizations for the current account
 * This extends the base account query to include all organizations data
 */
export const GetAccountWithOrganizations = graphql(/* GraphQL */ `
  query GetAccountWithOrganizations {
    account {
      id
      email
      organizations {
        id
        name
        ownerEmail
        channels {
          id
          name
          service
          isLocked
        }
        members {
          totalCount
        }
        billing {
          __typename
          ... on OBBilling {
            subscription {
              plan {
                id
                name
                interval
              }
            }
          }
        }
      }
    }
  }
`)

/**
 * GraphQL mutation to set the current organization
 */
export const SetCurrentOrganization = graphql(/* GraphQL */ `
  mutation SetCurrentOrganization($organizationId: String) {
    accountSetCurrentOrganization(organizationId: $organizationId)
  }
`)

// Extract the organization type from the query result
type OrganizationFromQuery = NonNullable<
  ResultOf<typeof GetAccountWithOrganizations>['account']
>['organizations'][number]

export type SwitchOrganizationOptions = {
  /** Where to land instead of home, for callers switching on the way somewhere. */
  redirectTo?: string
}

export type UseOrganizationSwitcherReturn = {
  organizations: OrganizationFromQuery[]
  currentOrganizationId: string | null
  loading: boolean
  error: boolean
  switchOrganization: (
    organizationId: string,
    options?: SwitchOrganizationOptions,
  ) => Promise<void>
}

/**
 * Hook for managing organization switching in the Publish app
 *
 * @example
 * ```tsx
 * const { organizations, switchOrganization, loading } = useOrganizationSwitcher()
 *
 * const handleSwitch = async (orgId: string) => {
 *   await switchOrganization(orgId)
 * }
 *
 * await switchOrganization(orgId, { redirectTo: '/channels/abc/schedule/123' })
 * ```
 */
export const useOrganizationSwitcher = (): UseOrganizationSwitcherReturn => {
  const { account, refetchAccount } = useAccount()
  const navigate = useNavigate()
  const commonTrackingProperties = useCommonTrackingProperties()

  // Fetch all organizations
  const {
    data: organizationsData,
    loading: organizationsLoading,
    error: organizationsError,
  } = useQuery(GetAccountWithOrganizations)

  // Mutation to switch organization
  const [setCurrentOrganizationMutation, { loading: switchLoading }] =
    useMutation(SetCurrentOrganization)

  /**
   * Switch to a different organization
   * After switching, refetch account data and navigate appropriately
   */
  const switchOrganization = useCallback(
    async (
      organizationId: string,
      { redirectTo }: SwitchOrganizationOptions = {},
    ): Promise<void> => {
      try {
        await setCurrentOrganizationMutation({
          variables: { organizationId },
          refetchQueries: [GetMainSidebarInfo],
          awaitRefetchQueries: true,
        })

        // Refetch account to get updated currentOrganization
        await refetchAccount?.()

        // Track organization switch
        BufferTracker.organizationSwitched({
          ...commonTrackingProperties,
          organizationId,
          product: 'publish',
        })

        // Hard refresh for settings pages (they need full data reload)
        // Navigate home for other pages
        // This matches the legacy app-shell behavior
        if (window.location.pathname.startsWith('/settings')) {
          window.location.reload()
          return
        }
        navigate(redirectTo ?? '/', { replace: true })
      } catch (error) {
        toast.error('Error switching organization')
        debugLog('❌ Failed to switch organization:', error)
        throw error
      }
    },
    [
      setCurrentOrganizationMutation,
      refetchAccount,
      navigate,
      commonTrackingProperties,
    ],
  )

  // Return organizations directly from the GraphQL query
  const organizations: OrganizationFromQuery[] =
    organizationsData?.account?.organizations ?? []

  return {
    organizations,
    currentOrganizationId: account?.currentOrganization?.id ?? null,
    // Cold-load only for the query flag: background refetches keep cached
    // organizations rendering. An explicit switch (switchLoading) is a real
    // transition and still shows loading UI.
    loading:
      (organizationsLoading && !organizationsData?.account) || switchLoading,
    error: !!organizationsError,
    switchOrganization,
  }
}
